<script lang="ts">
import { defineComponent, PropType, ref, computed, nextTick } from 'vue'
import StepLayout from './StepLayout.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import RadioButtonDefault from '@/components/form/RadioButtonDefault.vue'
import CheckboxDefault from '@/components/form/CheckboxDefault.vue'
import { formPartial } from '@/models/formPartial'
import validationOperator from '@/models/validationOperator'

export default defineComponent({
  name: 'StepWrapper',

  components: {
    StepLayout,
    ValidationObserver,
    RadioButtonDefault,
    CheckboxDefault,
    ButtonSubmit,
  },

  props: {
    namespace: {
      type: String,
      required: true,
    },

    /**
     * 項目の入力オブジェクト
     */
    inputScheme: {
      type: Object as PropType<InputText | InputMultiSelect>,
      required: true,
    },

    /**
     * 見出しに表示する項目ラベル
     * @example scale の場合 -> 「従業員数」
     *          実際に画面に表示される見出し文言 -> 「お勤め先の従業員数を教えて下さい」
     */
    categoryLabel: {
      type: String,
      required: true,
    },

    /**
     * 選択肢に表示する項目定数の配列
     */
    categoryItems: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  emits: ['update:modelValue', 'click:prev', 'click:next', 'error'],

  setup(props, { emit }) {
    // constants
    const MAX_STEP = 6 as const

    // model
    const _validationScheme = ref(props.inputScheme.validationScheme || {})
    const { createErrorMessage } = formPartial(props.inputScheme.name, _validationScheme)
    const { validationObserver, validate } = validationOperator()

    /**
     * template の type error を避けるため、computed で props をラップする
     *
     * @description
     *  isSingleSelect の computed を、直接 !Array.isArray(props.inputScheme.value) とすると、
     *  template 側で type error が発生する。
     *  これは、computed が単に真偽値のみを返却するだけで、実際の computed の中身の情報までは提供しないため、template 側が型を推論できないためである。
     */
    const singleValue = computed<string | number | undefined>(() => {
      return !Array.isArray(props.inputScheme.value)
        ? props.inputScheme.value
        : undefined
    })

    const multiValue = computed<Array<string | number> | undefined>(() => {
      return Array.isArray(props.inputScheme.value)
        ? props.inputScheme.value
        : undefined
    })

    /**
     * 単一選択の質問項目かどうか
     */
    const isSingleSelect = computed(() => singleValue.value !== undefined)

    const selectItems = computed(() => {
      return props.categoryItems.map(v => {
        return {
          label: v,
          value: v,
        }
      })
    })

    const next = () => {
      emit('click:next')
    }

    /**
     * 単一選択の場合の項目選択時の処理
     *
     * @param value 選択された項目の値
     */
    const onClickItem = async (value: string) => {
      /**
       * 同項目の選択だったらそのまま次へ遷移（次ステップから戻ってきた場合の同項目選択）
       */
      if (props.inputScheme.value === value) {
        next()
        return
      }

      emit('update:modelValue', value)

      // 親に値がバインドされたのを待ってからバリデーション発火
      nextTick(() => {
        validate()
      })
    }

    /**
     * 複数選択の場合の次へボタン押下時の処理
     */
    const onSubmit = () => {

      /**
       * 同項目の選択だったらそのまま次へ遷移（次ステップから戻ってきた場合の同項目選択）
       */
      if (!props.inputScheme.value.length) {
        return
      }

      validate()
    }

    return {
      // constants
      MAX_STEP,

      // data
      validationObserver,

      // computed
      selectItems,
      singleValue,
      multiValue,
      isSingleSelect,

      // methods
      createErrorMessage,
      onClickItem,
      onSubmit,
      next,
    }
  },
})
</script>

<template>
  <StepLayout
    :required="inputScheme.validationScheme?.required"
    @click:prev="$emit('click:prev')">
    <template #title>
      お勤め先の{{ categoryLabel }}を教えて下さい
    </template>
    <template #caption>
      {{ isSingleSelect ? '1つ選択してください' : '複数選択してください' }}
    </template>
    <template #description>
      <ValidationObserver
        ref="validationObserver"
        :namespace="namespace"
        @valid="next">
        <!-- 単一選択項目 -->
        <template v-if="isSingleSelect">
          <ValidationProvider
            :value="inputScheme.value"
            :namespace="namespace"
            :field="inputScheme.name"
            :scheme="inputScheme.validationScheme"
            :messages="createErrorMessage(true, false, categoryLabel)"
            @error="$emit('error', $event)">
            <RadioButtonDefault
              :model-value="singleValue"
              :name="inputScheme.name"
              :items="selectItems"
              :cols="2"
              @click:item="onClickItem($event)" />
          </ValidationProvider>
        </template>

        <!-- 複数選択項目 -->
        <template v-else-if="!!multiValue">
          <ValidationProvider
            :value="inputScheme.value"
            :namespace="namespace"
            :field="inputScheme.name"
            :scheme="inputScheme.validationScheme"
            :messages="createErrorMessage(false, true, categoryLabel)"
            @error="$emit('error', $event)">
            <CheckboxDefault
              :model-value="multiValue"
              :name="inputScheme.name"
              :items="selectItems"
              :cols="2"
              @update:model-value="$emit('update:modelValue', $event)" />
          </ValidationProvider>

          <ButtonSubmit
            :is-link="false"
            :has-shadow="false"
            :width="384"
            :disabled="!inputScheme.value.length"
            prevent
            stop
            @click:submit="onSubmit">
            {{ multiValue.length > 1 ? '全て選択したので次へ' : '複数選択してください' }}
          </ButtonSubmit>
        </template>
      </ValidationObserver>
    </template>
  </StepLayout>
</template>
